import { createApiClient, getRuntimeConfig, useAuth, type ApiClient } from '@berky/auth'
import { useMemo } from 'react'

export interface ModuleUsageRow {
  usageDate: string
  userId: string
  userName: string
  userEmail: string
  moduleId: string
  usageCount: number
  lastUsedAt: string
  actionCount: number
  changedRecordCount: number
  lastActionAt: string | null
}

export interface ModuleUsagePage {
  items: ModuleUsageRow[]
  total: number
  page: number
  pageSize: number
}

export interface UsageSettings {
  testMode: boolean
  version: string
  detailTrackingSince: string | null
}

export interface UsagePageResult<T> {
  items: T[]
  total: number
  page: number
  pageSize: number
}

export interface UsageDay {
  date: string
  users: number
  entries: number
}

export interface UsageSummary {
  from: string
  to: string
  previousFrom: string
  previousTo: string
  activeUsers: number
  previousActiveUsers: number
  entries: number
  previousEntries: number
  actions: number
  previousActions: number
  changedRecords: number
  previousChangedRecords: number
  knownUsers: number
  activeKnownUsers: number
  returningUsers: number
  todayUsers: number
  weekUsers: number
  days: UsageDay[]
}

export interface UsageAdoptionPerson {
  userId: string
  userName: string
  userEmail: string
  lastAccessAt: string | null
  lastModuleId: string | null
  entries: number
  activeDays: number
  modules: { moduleId: string; entries: number }[]
  actions: number
  changedRecords: number
}

export interface UsageActivityRow {
  usageDate: string
  moduleId: string
  entries: number
  lastAccessAt: string
  actions: number
  changedRecords: number
}

export interface UsageModuleRank {
  moduleId: string
  users: number
  entries: number
}

export interface UsagePageRank {
  pagePath: string
  users: number
  visits: number
  activeSeconds: number
}

/** Platform client for recording and reporting module entries. */
export function usePlatformUsageApi(): ApiClient {
  const { getAccessToken, reportApiAuthError } = useAuth()
  const { platformScope } = getRuntimeConfig()
  const scopes = useMemo(() => (platformScope ? [platformScope] : []), [platformScope])

  return useMemo(
    () =>
      createApiClient({
        baseUrl: '/api/platform/usage',
        scopes,
        getAccessToken,
        onAuthError: reportApiAuthError,
      }),
    [scopes, getAccessToken, reportApiAuthError],
  )
}
